import { useCallback, useEffect, useRef, useState } from "react";
import useDataset from "datasets/hooks/useDataset";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { Check, Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";
import useErrorToast from "~/contexts/error-toast";
import { trackEvent } from "~/utils/monitoring";

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

const MAX_LENGTH = 50_000;

type FetchState = "loading" | "ready" | "error";
type SaveState = "idle" | "saving" | "saved";

interface AgentInstructionsEditorProps {
  // Set when rendered outside the dataset route tree (the session context
  // sidebar), which has no :datasetId route param to resolve the dataset from.
  datasetId?: string;
  // Forces display-only mode regardless of ownership — set by read-only surfaces
  // such as the session context sidebar, where editing happens on the full page.
  readOnly?: boolean;
}

const AgentInstructionsEditor = ({
  datasetId,
  readOnly = false,
}: AgentInstructionsEditorProps = {}) => {
  const { data: dataset } = useDataset(datasetId);
  const { showError } = useErrorToast();
  const canEdit = dataset?.access_level === DatasetAccessLevel.OWNER && !readOnly;

  const [fetchState, setFetchState] = useState<FetchState>("loading");
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [instructions, setInstructions] = useState("");
  const savedRef = useRef("");
  const abortRef = useRef<AbortController | null>(null);

  const isDirty = instructions !== savedRef.current;

  const fetchInstructions = useCallback(
    async (datasetId: string) => {
      setFetchState("loading");
      try {
        const resp = await fetch(
          `${VITE_ANALYSIS_AGENT_API}/datasets/${datasetId}/agent-instructions`
        );

        if (resp.status === 404) {
          savedRef.current = "";
          setInstructions("");
          setFetchState("ready");
          return;
        }

        if (!resp.ok) {
          throw new Error("Failed to fetch agent instructions");
        }

        const data = await resp.json();
        const text = data.agent_instructions ?? "";
        savedRef.current = text;
        setInstructions(text);
        setFetchState("ready");
      } catch (e) {
        console.error(e);
        setFetchState("error");
        showError("Unable to load agent instructions");
      }
    },
    [showError]
  );

  useEffect(() => {
    if (dataset?.id) {
      fetchInstructions(dataset.id);
    }
  }, [dataset?.id, fetchInstructions]);

  const handleSave = async () => {
    if (!dataset || !isDirty) {
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setSaveState("saving");

    try {
      const resp = await fetch(
        `${VITE_ANALYSIS_AGENT_API}/datasets/${dataset.id}/agent-instructions`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ agent_instructions: instructions }),
          signal: controller.signal,
        }
      );

      if (!resp.ok) {
        throw new Error("Failed to save agent instructions");
      }

      savedRef.current = instructions;
      setSaveState("saved");

      trackEvent("dataset_agent_instructions_saved", {
        dataset_id: dataset.id,
        length: instructions.length,
      });
    } catch (e) {
      if ((e as Error).name === "AbortError") {
        return;
      }
      console.error(e);
      setSaveState("idle");
      showError("Unable to save agent instructions");
    }
  };

  const handleCancel = () => {
    setInstructions(savedRef.current);
    setSaveState("idle");
  };

  const handleChange = (value: string) => {
    setInstructions(value);
    setSaveState("idle");
  };

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  if (fetchState === "loading") {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (fetchState === "error") {
    return (
      <div className="flex flex-col gap-2 py-2">
        <p className="text-sm text-destructive">Failed to load agent instructions.</p>
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => dataset?.id && fetchInstructions(dataset.id)}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Help the AI agents understand your data better. Add any context that might be useful, such
        as how the data is structured, known quirks, preferred analysis approaches, or relevant
        domain knowledge. This will be included in every session.
      </p>
      <Textarea
        placeholder="e.g. This dataset uses NAACCR coding standards. The 'site' column contains ICD-O-3 topography codes. Exclude rows where status = 'withdrawn' unless explicitly asked."
        value={instructions}
        disabled={!canEdit}
        onChange={(e) => handleChange(e.target.value)}
        maxLength={MAX_LENGTH}
        className="min-h-[9.5rem] max-h-[30rem] overflow-y-auto"
      />
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">
          {instructions.length.toLocaleString()} / {MAX_LENGTH.toLocaleString()}
        </span>
        {saveState === "saved" && !isDirty && (
          <div className="flex items-center gap-1">
            <Check className="size-4 text-green-600" />
            <span className="text-xs text-green-600">Saved</span>
          </div>
        )}
      </div>
      {canEdit && isDirty && (
        <div className="flex gap-2">
          <Button size="sm" loading={saveState === "saving"} onClick={handleSave}>
            Save
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={saveState === "saving"}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

export default AgentInstructionsEditor;
